import type { SolicitudActividad } from './solicitudActividad';
import { congelarSolicitudActividad, ResultadoActividadInciertoError, RPC_SOLICITUDES_ACTIVIDAD } from './solicitudActividad';

export interface OperacionActividadPendiente {
  readonly solicitud: SolicitudActividad;
  readonly enviando: boolean;
}

export function recuperarOperacionActividad(actor: string): {
  readonly pendiente: OperacionActividadPendiente | null; readonly error: string | null;
} {
  try { return { pendiente: leerOperacionActividad(actor), error: null }; }
  catch { return { pendiente: null, error: 'No pudimos recuperar la operación pendiente. Conserva esta pestaña y solicita ayuda.' }; }
}

/** Una respuesta de una sesión desmontada no modifica la pantalla de otra. */
export class VigenciaOperacionActividad {
  private epoca = 0;
  private activa = true;
  constructor(readonly actor: string) {}
  activar(): void { this.epoca++; this.activa = true; }
  invalidar(): void { this.epoca++; this.activa = false; }
  capturar(): { readonly actor: string; readonly epoca: number } {
    return { actor: this.actor, epoca: this.epoca };
  }
  vigente(captura: { readonly actor: string; readonly epoca: number }): boolean {
    return this.activa && captura.actor === this.actor && captura.epoca === this.epoca;
  }
}

// Solo datos de la operación, nunca JWT/credenciales. La clave separa actores;
// sessionStorage conserva incertidumbre al cambiar de pantalla o recargar pestaña.
const pendientes = new Map<string, OperacionActividadPendiente | null>();
const oyentes = new Set<() => void>();
const clave = (actor: string) => `actividad-pendiente-v1:${actor}`;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function objeto(valor: unknown): valor is Record<string, unknown> {
  return valor !== null && typeof valor === 'object' && !Array.isArray(valor);
}

export function leerOperacionActividad(actor: string): OperacionActividadPendiente | null {
  if (pendientes.has(actor)) return pendientes.get(actor) ?? null;
  const guardada = window.sessionStorage.getItem(clave(actor));
  if (!guardada) { pendientes.set(actor, null); return null; }
  const dato: unknown = JSON.parse(guardada);
  if (!objeto(dato) || typeof dato.id !== 'string' || !uuid.test(dato.id)
    || typeof dato.rpc !== 'string' || typeof dato.operacion !== 'string'
    || !Object.prototype.hasOwnProperty.call(RPC_SOLICITUDES_ACTIVIDAD, dato.operacion)
    || RPC_SOLICITUDES_ACTIVIDAD[dato.operacion] !== dato.rpc
    || !objeto(dato.argumentos) || !objeto(dato.argumentosContrato)
    || 'p_solicitud' in dato.argumentos) {
    throw new Error('No pudimos recuperar la operación pendiente. Conserva esta pestaña y solicita ayuda.');
  }
  // El servidor valida RPC, actor, permisos y payload. Lo almacenado no es un
  // permiso ni una bandera para saltar RLS; solo preserva identidad del reintento.
  const solicitud = congelarSolicitudActividad(dato as unknown as SolicitudActividad);
  const pendiente = { solicitud, enviando: false };
  pendientes.set(actor, pendiente);
  return pendiente;
}

export function comenzarOperacionActividad(actor: string, solicitud: SolicitudActividad): boolean {
  const anterior = leerOperacionActividad(actor);
  if (anterior && (anterior.enviando || JSON.stringify(anterior.solicitud) !== JSON.stringify(solicitud))) {
    throw new ResultadoActividadInciertoError(anterior.solicitud, 'Hay una operación por verificar');
  }
  // Fallar ANTES de la solicitud si el navegador no permite conservarla.
  window.sessionStorage.setItem(clave(actor), JSON.stringify(solicitud));
  pendientes.set(actor, { solicitud, enviando: true });
  oyentes.forEach((avisar) => avisar());
  return anterior !== null;
}

export function conservarOperacionActividad(actor: string, solicitud: SolicitudActividad): void {
  pendientes.set(actor, { solicitud, enviando: false });
  oyentes.forEach((avisar) => avisar());
}

export function resolverOperacionActividad(actor: string, id: string): void {
  const anterior = leerOperacionActividad(actor);
  if (anterior?.solicitud.id !== id) return;
  try {
    window.sessionStorage.removeItem(clave(actor));
  } catch (error: unknown) {
    conservarOperacionActividad(actor, anterior.solicitud);
    throw new ResultadoActividadInciertoError(anterior.solicitud, error);
  }
  pendientes.set(actor, null);
  oyentes.forEach((avisar) => avisar());
}

export function observarOperacionActividad(avisar: () => void): () => void {
  oyentes.add(avisar);
  return () => { oyentes.delete(avisar); };
}
